import React, { useState } from 'react';
import BasePageLayout from '../components/BasePageLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import '../styles/pages/HelpPage.css';
import '../styles/layout/wide-screen-layouts.css';
import '../styles/shared/dashboard.css';

const HelpPage: React.FC = () => {
  const { t } = useLanguage();
  const { user, resetTour, tourLoading } = useAuth();
  const [tourResetSuccess, setTourResetSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRestartTour = async () => {
    if (!user) {
      setError(t('helpPage.tourResetError'));
      return;
    }

    try {
      setError(null);
      setTourResetSuccess(null);
      const result = await resetTour();
      
      if (result.success) {
        setTourResetSuccess(t('helpPage.tourResetSuccess'));
      } else {
        setError(result.error?.message || 'Failed to reset tour');
      }
    } catch (err: any) {
      console.error('Error restarting tour:', err);
      setError(err.message || 'Failed to reset tour');
    }
  };
  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="help-container">
        {/* Header Section - Matching Points History Page */}
        <section className="help-header-section">
          <div className="help-header-content">
            <div className="help-hero-centered">
              <div className="help-icon-wrapper">
                <div className="help-icon-large">
                  <i className="bi bi-question-circle"></i>
                </div>
              </div>
              
              <div className="help-info">
                <div className="help-title-group">
                  <h1 className="help-title-main">{t('helpPage.title')}</h1>
                  <span className="help-badge">
                    <i className="bi bi-life-preserver"></i>
                    {t('helpPage.support247')}
                  </span>
                </div>
                
                <p className="help-description">
                  {t('helpPage.description')}
                </p>
                
                <div className="help-meta">
                  <div className="meta-item">
                    <i className="bi bi-book"></i>
                    <span>{t('helpPage.comprehensiveGuides')}</span>
                  </div>
                  <div className="meta-item">
                    <i className="bi bi-headset"></i>
                    <span>{t('helpPage.expertSupport')}</span>
                  </div>
                  <div className="meta-item">
                    <i className="bi bi-people"></i>
                    <span>{t('helpPage.communityHelp')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        
        {/* Main Content Grid - Similar to ProfilePage wide-grid */}
        <section className="help-wide-grid">
          {/* Getting Started Panel */}
          <div className="help-panel-primary">
            <div className="help-info-card">
              <h3 className="section-title">
                <i className="bi bi-rocket-takeoff"></i>
                {t('helpPage.gettingStarted')}
              </h3>
              
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {tourResetSuccess && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  {tourResetSuccess}
                </div>
              )}

              <div className="help-items help-cards-grid">
                {/* Take the Onboarding Tour Card - HIDDEN: Tour disabled due to mobile issues */}
                {/* <div className="help-item help-card">
                  <h4>{t('helpPage.takeOnboardingTour')}</h4>
                  <p>{t('helpPage.newToSeedDots')}</p>
                  <div className="tour-restart-section">
                    <div className="tour-benefits">
                      <div className="benefit-item">
                        <i className="bi bi-compass text-primary"></i>
                        <span>{t('helpPage.learnFeatures')}</span>
                      </div>
                      <div className="benefit-item">
                        <i className="bi bi-clock text-primary"></i>
                        <span>{t('helpPage.takesMinutes')}</span>
                      </div>
                      <div className="benefit-item">
                        <i className="bi bi-skip-forward text-primary"></i>
                        <span>{t('helpPage.skipAnytime')}</span>
                      </div>
                    </div>
                    {user ? (
                      <button
                        className="btn btn-primary mt-3"
                        onClick={handleRestartTour}
                        disabled={tourLoading}
                      >
                        {tourLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('helpPage.startingTour')}
                          </>
                        ) : (
                          <>
                            <i className="bi bi-play-circle me-2"></i>
                            {t('helpPage.startOnboardingTour')}
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="alert alert-info mt-3">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('helpPage.pleaseLogin')} <a href="/login">{t('helpPage.loginLink')}</a> {t('helpPage.toTakeTour')}
                      </div>
                    )}
                  </div>
                </div> */}

                {/* How to create your first task Card */}
                <div className="help-item help-card">
                  <h4>{t('helpPage.createFirstTask')}</h4>
                  <p>{t('helpPage.createFirstTaskDesc')}</p>
                  <div className="help-steps">
                    <ol>
                      <li>{t('helpPage.navigateToTasks')}</li>
                      <li>{t('helpPage.clickCreateTask')}</li>
                      <li>{t('helpPage.fillTaskDetails')}</li>
                      <li>{t('helpPage.chooseVerification')}</li>
                      <li>{t('helpPage.publishTask')}</li>
                    </ol>
                  </div>
                </div>
                
                {/* Understanding Points Card */}
                <div className="help-item help-card">
                  <h4>{t('helpPage.understandingPoints')}</h4>
                  <p>{t('helpPage.understandingPointsDesc')}</p>
                  <div className="points-info">
                    <div className="point-item">
                      <span className="point-action">{t('helpPage.completeTask')}</span>
                      <span className="point-value">{t('helpPage.completeTaskPoints')}</span>
                    </div>
                    <div className="point-item">
                      <span className="point-action">{t('helpPage.dailyLoginBonus')}</span>
                      <span className="point-value">{t('helpPage.dailyLoginPoints')}</span>
                    </div>
                    <div className="point-item">
                      <span className="point-action">{t('helpPage.referFriend')}</span>
                      <span className="point-value">{t('helpPage.referFriendPoints')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks & Points Panel */}
          <div className="help-panel-secondary">
            <div className="help-info-card">
              <h3 className="section-title">
                <i className="bi bi-list-task"></i>
                {t('helpPage.tasksAndPoints')}
              </h3>
              
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('helpPage.taskVerification')}</h4>
                  <p>{t('helpPage.taskVerificationDesc')}</p>
                  <ul>
                    <li>{t('helpPage.automaticVerification')}</li>
                    <li>{t('helpPage.communityVoting')}</li>
                    <li>{t('helpPage.manualReview')}</li>
                    <li>{t('helpPage.appealsProcess')}</li>
                  </ul>
                </div>
                
                <div className="help-item">
                  <h4>{t('helpPage.goodTask')}</h4>
                  <p>{t('helpPage.goodTaskDesc')}</p>
                  <ul>
                    <li>{t('helpPage.clearInstructions')}</li>
                    <li>{t('helpPage.reasonableTime')}</li>
                    <li>{t('helpPage.fairRewards')}</li>
                    <li>{t('helpPage.properVerification')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Teams & Collaboration Panel */}
          <div className="help-panel-tertiary">
            <div className="help-info-card">
              <h3 className="section-title">
                <i className="bi bi-people-fill"></i>
                {t('helpPage.teamsCollaboration')}
              </h3>
              
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('helpPage.findingTeamMembers')}</h4>
                  <p>{t('helpPage.findingTeamMembersDesc')}</p>
                  <div className="feature-highlight">
                    <i className="bi bi-lightbulb"></i>
                    <span>{t('helpPage.proTip')} {t('helpPage.proTipDesc')}</span>
                  </div>
                </div>
                
                <div className="help-item">
                  <h4>{t('helpPage.managingTeamRequests')}</h4>
                  <p>{t('helpPage.managingTeamRequestsDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Panel */}
          <div className="help-panel-faq">
            <div className="help-info-card">
              <h3 className="section-title">
                <i className="bi bi-question-octagon"></i>
                {t('helpPage.commonQuestions')}
              </h3>
              
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('helpPage.faq')}</h4>
                  <div className="faq-list">
                    <div className="faq-item">
                      <strong>{t('helpPage.faqNoResponses')}</strong>
                      <p>{t('helpPage.faqNoResponsesAnswer')}</p>
                    </div>
                    <div className="faq-item">
                      <strong>{t('helpPage.faqPointsNotShowing')}</strong>
                      <p>{t('helpPage.faqPointsNotShowingAnswer')}</p>
                    </div>
                    <div className="faq-item">
                      <strong>{t('helpPage.faqCantUpload')}</strong>
                      <p>{t('helpPage.faqCantUploadAnswer')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Support Panel */}
          <div className="help-panel-contact">
            <div className="help-info-card">
              <h3 className="section-title">
                <i className="bi bi-headset"></i>
                {t('helpPage.stillNeedHelp')}
              </h3>
              
              <div className="contact-options">
                <div className="contact-option">
                  <div className="contact-icon">
                    <i className="bi bi-chat-left-text"></i>
                  </div>
                  <div className="contact-content">
                    <strong>{t('helpPage.emailSupport')}</strong>
                    <p>{t('helpPage.emailSupportDesc')}</p>
                    <a href="/contact" className="btn btn-secondary">{t('helpPage.contactUs')}</a>
                  </div>
                </div>
                
                <div className="contact-option">
                  <div className="contact-icon">
                    <i className="bi bi-chat-dots"></i>
                  </div>
                  <div className="contact-content">
                    <strong>{t('helpPage.communityForum')}</strong>
                    <p>{t('helpPage.communityForumDesc')}</p>
                    <a href="/forums" className="btn btn-secondary">{t('helpPage.visitForum')}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </BasePageLayout>
  );
};

export default HelpPage;
